/**
 * lib/api.ts
 * ─────────────────────────────────────────────────────────────
 * Typed API client for all backend endpoints.
 * Updated: credentials: 'include' on every request so the
 * session cookie is sent automatically.
 * Global 401 handler reloads the page to trigger re-login.
 * ─────────────────────────────────────────────────────────────
 */

import type {
  Gapper, TechnicalSignals, OptionsChain, TradePlan,
  ScoreBreakdown, NewsItem, AccountStats, Trade,
  ScanRecord, ScanSummary,
  AutotraderStatus, AutotraderLogEntry,
  PnlResponse, PnlPeriod, PnlSource,
  ShadowSummary,
  ApiResponse,
} from '../types';

const BASE = '/api';

/** Generic fetch wrapper — unwraps ApiResponse envelope */
async function apiFetch<T>(
  path:    string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers:     { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',   // always send the session cookie
    ...options,
  });

  // Session expired or never authenticated — reload to trigger login page
  if (res.status === 401) {
    window.location.reload();
    throw new Error('Session expired');
  }

  const envelope = await res.json() as ApiResponse<T>;

  if (!res.ok || !envelope.success) {
    throw new Error(envelope.error ?? `Request failed: ${res.status}`);
  }

  return envelope.data as T;
}

// ─── Scanner ─────────────────────────────────────────────────

export const scannerApi = {
  run: (filters?: {
    minGapPct?: number;
    minRvol?:   number;
    minPrice?:  number;
    maxPrice?:  number;
  }) => apiFetch<Gapper[]>('/scanner/run', {
    method: 'POST',
    body:   JSON.stringify(filters ?? {}),
  }),

  history: (date?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (date)  params.set('date',  date);
    if (limit) params.set('limit', String(limit));
    return apiFetch<Gapper[]>(`/scanner/history?${params}`);
  },

  watchlist: {
    get: () => apiFetch<Array<{ sym: string; name: string; notes?: string; added_at: string }>>('/scanner/watchlist'),
    add: (sym: string, name?: string, notes?: string) =>
           apiFetch('/scanner/watchlist', { method: 'POST', body: JSON.stringify({ sym, name, notes }) }),
    remove: (sym: string) => apiFetch(`/scanner/watchlist/${sym}`, { method: 'DELETE' }),
  },
};

// ─── Analysis ────────────────────────────────────────────────

export const analysisApi = {
  signals: (sym: string) =>
    apiFetch<TechnicalSignals>(`/analysis/${sym}/signals`),

  options: (sym: string, expiry?: string, price?: number) => {
    const params = new URLSearchParams();
    if (expiry) params.set('expiry', expiry);
    if (price)  params.set('price',  price.toFixed(2));
    const qs = params.toString() ? `?${params}` : '';
    return apiFetch<OptionsChain>(`/analysis/${sym}/options${qs}`);
  },

  plan: (sym: string, price?: number, expiry?: string) => {
    const params = new URLSearchParams();
    if (price)  params.set('price',  String(price));
    if (expiry) params.set('expiry', expiry);
    return apiFetch<TradePlan>(`/analysis/${sym}/plan?${params}`);
  },

  news: (sym: string) =>
    apiFetch<NewsItem[]>(`/analysis/${sym}/news`),

  ai: (sym: string, body: { gapper: Gapper; signals: TechnicalSignals; score: ScoreBreakdown; bestCall?: unknown }) =>
    apiFetch<{ analysis: string; cached: boolean }>(`/analysis/${sym}/ai`, {
      method: 'POST',
      body:   JSON.stringify(body),
    }),

  chat: (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    context?: { sym?: string; changePct?: number; score?: number }
  ) => apiFetch<{ reply: string }>('/analysis/chat', {
    method: 'POST',
    body:   JSON.stringify({ messages, context }),
  }),
};

// ─── Trades ──────────────────────────────────────────────────

export const tradesApi = {
  list: (filters?: { status?: string; outcome?: string; sym?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status)  params.set('status',  filters.status);
    if (filters?.outcome) params.set('outcome', filters.outcome);
    if (filters?.sym)     params.set('sym',     filters.sym);
    if (filters?.limit)   params.set('limit',   String(filters.limit));
    return apiFetch<Trade[]>(`/trades?${params}`);
  },

  create: (trade: {
    sym:                 string;
    contractDescription: string;
    catalystSummary?:    string;
    catalystType?:       string;
    entryStockPrice:     number;
    entryOptionPrice:    number;
    contracts?:          number;
    plannedStop:         number;
    plannedTarget1:      number;
    plannedTarget2?:     number;
    plannedRR:           number;
    score?:              number;
    notes?:              string;
  }) => apiFetch<Trade>('/trades', { method: 'POST', body: JSON.stringify(trade) }),

  get:    (id: string) => apiFetch<Trade>(`/trades/${id}`),

  update: (id: string, updates: Partial<Trade & {
    exitDate?:        string;
    exitOptionPrice?: number;
    exitReason?:      string;
    emotionScore?:    number;
    rulesFollowed?:   boolean;
    lessons?:         string;
  }>) => apiFetch<Trade>(`/trades/${id}`, {
    method: 'PATCH',
    body:   JSON.stringify(updates),
  }),

  delete: (id: string) => apiFetch(`/trades/${id}`, { method: 'DELETE' }),
  stats:  () => apiFetch<AccountStats>('/trades/stats/summary'),
};

// ─── Account ─────────────────────────────────────────────────

export const accountApi = {
  settings: {
    get:    () => apiFetch<Record<string, { value: string; label: string; type: string }>>('/account/settings'),
    update: (updates: Record<string, string>) =>
              apiFetch('/account/settings', { method: 'PATCH', body: JSON.stringify(updates) }),
  },

  balance: {
    get: () => apiFetch<{
      balance: number; startingBalance: number; totalPnl: number; totalPnlPct: number;
      maxRiskPerTrade: number; riskPct: number; phase: number;
      phaseTarget: number; progressToNext: number;
    }>('/account/balance'),

    set: (balance: number, note?: string) =>
           apiFetch('/account/balance', { method: 'POST', body: JSON.stringify({ balance, note }) }),
  },

  snapshots: (days?: number) => {
    const params = days ? `?days=${days}` : '';
    return apiFetch<Array<{ date: string; balance: number; today_pnl: number; phase: number }>>(
      `/account/snapshots${params}`
    );
  },
};

// ─── Analytics (scan log) ──────────────────────────────────────

export const analyticsApi = {
  scans: (filters?: {
    date?:     string;
    sym?:      string;
    minScore?: number;
    catalyst?: string;
    outcome?:  string;
    source?:   'manual' | 'auto';
    limit?:    number;
    offset?:   number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.date)          params.set('date',     filters.date);
    if (filters?.sym)           params.set('sym',      filters.sym);
    if (filters?.minScore)      params.set('minScore', String(filters.minScore));
    if (filters?.catalyst)      params.set('catalyst', filters.catalyst);
    if (filters?.outcome)       params.set('outcome',  filters.outcome);
    if (filters?.source)        params.set('source',   filters.source);
    if (filters?.limit)         params.set('limit',    String(filters.limit));
    if (filters?.offset != null) params.set('offset',  String(filters.offset));
    return apiFetch<{ rows: ScanRecord[]; total: number; limit: number; offset: number }>(
      `/analytics/scans?${params}`
    );
  },

  summary: () => apiFetch<ScanSummary[]>('/analytics/scans/summary'),

  get: (id: string) => apiFetch<ScanRecord>(`/analytics/scans/${id}`),

  update: (id: string, updates: { outcome?: string; actual_move_pct?: number; notes?: string }) =>
    apiFetch<ScanRecord>(`/analytics/scans/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),

  delete: (id: string) => apiFetch(`/analytics/scans/${id}`, { method: 'DELETE' }),

  deleteByDate:    (date: string) => apiFetch(`/analytics/scans?date=${date}`, { method: 'DELETE' }),
  deleteOlderThan: (days: number) => apiFetch(`/analytics/scans?olderThan=${days}`, { method: 'DELETE' }),

  outcomes: () => apiFetch<{
    byScore:    Array<{ score_bracket: string; total: number; wins: number; losses: number; skipped: number; avg_move_pct: number | null; avg_gap_pct: number; avg_rvol: number }>;
    byCatalyst: Array<{ catalyst_type: string; total: number; wins: number; losses: number; avg_score: number }>;
  }>('/analytics/outcomes'),
};

// ─── Auto-Trader ─────────────────────────────────────────────

export const autotraderApi = {
  status: () => apiFetch<AutotraderStatus>('/autotrader/status'),

  enable:  () => apiFetch<{ enabled: boolean; mode: string }>('/autotrader/enable',  { method: 'POST' }),
  disable: () => apiFetch<{ enabled: boolean }>('/autotrader/disable', { method: 'POST' }),

  setMode: (mode: 'paper' | 'live', confirmPhrase?: string) =>
    apiFetch<{ mode: string; enabled: boolean }>('/autotrader/mode', {
      method: 'POST',
      body:   JSON.stringify({ mode, confirmPhrase }),
    }),

  log: (limit = 50) => apiFetch<AutotraderLogEntry[]>(`/autotrader/log?limit=${limit}`),

  pnl: (period: PnlPeriod = 'all', source: PnlSource = 'all') =>
    apiFetch<PnlResponse>(`/autotrader/pnl?period=${period}&source=${source}`),

  shadow: (days = 30) => apiFetch<ShadowSummary>(`/autotrader/shadow?days=${days}`),
};

// ─── Health ──────────────────────────────────────────────────

export const healthApi = {
  check: () => fetch('/health', { credentials: 'include' }).then(r => r.json()),
};
